import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  alpha,
  useTheme,
  Stack
} from '@mui/material';

const MetricStatCard = ({
  icon,
  title,
  value,
  helperText,
  trend = { direction: 'up', label: '+0%' },
  accent = 'primary'
}) => {
  const theme = useTheme();
  const paletteMap = {
    primary: theme.palette.primary,
    success: theme.palette.success,
    warning: theme.palette.warning,
    error: theme.palette.error,
    info: theme.palette.info
  };
  const accentPalette = paletteMap[accent] || theme.palette.primary;

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accentPalette.main, 0.08)}, ${alpha(accentPalette.main, 0.02)})`,
        border: accent === 'primary' 
          ? `1px solid ${theme.palette.grey[400]}`
          : `1px solid ${accentPalette.main}`,
        borderRadius: 3,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(accentPalette.main, 0.25)}`,
          bgcolor: 'white'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(accentPalette.main, 0.03)}, transparent)`,
          zIndex: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover::before': {
          opacity: 0.8
        }
      }}
    >
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 500,
                color: accent === 'primary' ? 'primary.main' : `${accent}.main`,
                mb: 1,
                fontSize: accent === 'warning' || accent === 'success' ? '1.5rem' : '2rem',
                lineHeight: 1
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                fontWeight: 500,
                fontSize: '1rem'
              }}
            >
              {title}
            </Typography>
            {helperText && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}
              >
                {helperText}
              </Typography>
            )}
            {trend.label && (
              <Typography
                variant="body2"
                color={trend.direction === 'up' ? 'success.main' : trend.direction === 'down' ? 'error.main' : 'text.secondary'}
                sx={{ fontWeight: 500, mt: 0.5, fontSize: '0.875rem' }}
              >
                {trend.label}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: accent === 'primary'
                ? `linear-gradient(${alpha(accentPalette.main, 0.1)}, ${alpha(accentPalette.main, 0.05)})`
                : accentPalette.main,
              color: accent === 'primary' ? 'text.secondary' : 'white',
              border: accent === 'primary'
                ? `1px solid ${alpha(accentPalette.main, 0.2)}`
                : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              backdropFilter: accent === 'primary' ? 'blur(10px)' : 'none',
              boxShadow: accent === 'primary'
                ? `0 8px 24px ${alpha(accentPalette.main, 0.3)}`
                : `0 6px 18px ${alpha(accentPalette.main, 0.3)}`
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MetricStatCard;

